import { Formik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/inputs/text-input";
import { RadioGroup } from "@/components/inputs/radio";
import { KycUpgradeError, useUpgradeKYC } from "@/hooks/useUpgradeKYC";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectIsKycPlatinumLevelDialogOpen,
  selectKycNextLevelRequirements,
  selectUserId,
  selectKycLocks,
  closeDialog,
  recordKycAttemptError,
  clearKycAttemptState,
  selectKycErrors,
  clearExpiredLocks,
} from "@/store/profileSlice";
import { GovernmentID } from "@/schemas/kyc";
import { ImageFileOrUrl } from "@/components/inputs/image-input";
import { CameraCapture } from "./camera-capture";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import Trust from "./trust";
import { fireCustomMixpanelEvent } from "@/events/fireCustomMixpanelEvent";
import { v4 as uuid } from "uuid";

export const REQUIREMENTS_MAP: Record<GovernmentID, string> = {
  ng_bvn: "BVN",
  ng_vnin: "vNIN",
  ng_nin: "NIN",
  gh_passport: "Passport",
  gh_ssnit: "SSNIT",
  gh_voters_card: "Voters Card",
  gh_drivers_license: "Drivers License",
};

export const getDocumentLabel = (docType: GovernmentID | null) => {
  if (docType) return REQUIREMENTS_MAP[docType];
  return docType;
};

export interface KycFormFields {
  documentVal: string;
  user_id: string;
  tier: string;
  documentType: GovernmentID;
  kycImage?: ImageFileOrUrl;
}

const step1Schema = Yup.object().shape({
  documentType: Yup.string().required("Please select a document type"),
});

const step2Schema = Yup.object().shape({
  documentVal: Yup.string()
    .required("Document value is required")
    .trim()
    .test(
      "valid-format",
      "Invalid format for selected document",
      function (value) {
        const { documentType } = this.parent;
        if (!value) return false;

        switch (documentType) {
          case "ng_bvn":
          case "ng_nin":
          case "ng_vnin":
            return /^\d{11}$/.test(value);
          default:
            return value.length > 0;
        }
      },
    ),
});

const step3Schema = Yup.object().shape({
  kycImage: Yup.mixed<ImageFileOrUrl>()
    .required("Please capture or upload your photo")
    .test(
      "is-file-or-string",
      "Selected value is not a file",
      (value) => value?.type === "file" || value?.type === "url",
    )
    .test("file-size", "File is too large. Maximum size is 5MB", (value) => {
      if (value?.type === "file") {
        return value.file.size <= 5 * 1024 * 1024;
      }
      return true;
    })
    .test(
      "file-type",
      "Unsupported file format. Please upload a JPEG or PNG image",
      (value) => {
        if (value?.type === "file") {
          return ["image/jpeg", "image/png"].includes(value.file.type);
        }
        return true;
      },
    ),
});

export function KycPlatinumLevelDialog() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [documentType, setDocumentType] = useState<GovernmentID | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const open = useAppSelector(selectIsKycPlatinumLevelDialogOpen);
  const nextLevelRequirements = useAppSelector(selectKycNextLevelRequirements);
  const userId = useAppSelector(selectUserId);
  const kycLocks = useAppSelector(selectKycLocks);
  const kycErrors = useAppSelector(selectKycErrors);
  const dispatch = useAppDispatch();
  const { mutateAsync } = useUpgradeKYC();

  useEffect(() => {
    if (open) {
      dispatch(clearExpiredLocks());
    }
  }, [open, dispatch]);

  const documentOptions =
    nextLevelRequirements
      ?.filter((docType) => {
        const lock = kycLocks[docType];
        return !lock || Date.now() > lock.lockedUntil;
      })
      ?.map((docType) => ({
        value: docType,
        label: REQUIREMENTS_MAP[docType] || docType,
        id: docType,
      })) || [];

  const activeLock = documentType ? kycLocks[documentType] : null;
  const isOptionCurrentlyLocked = !!(activeLock && Date.now() < activeLock.lockedUntil);

  const handleSubmitData = async (
    values: KycFormFields,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    const payload = {
      ...values,
      user_id: userId ?? "",
    };

    try {
      await mutateAsync(payload);

      dispatch(clearKycAttemptState({ documentType: values.documentType }));

      fireCustomMixpanelEvent(
        "CompleteKyc",
        userId,
        uuid(),
        { kyc_level: "platinum" }
      ).catch(() => {});

      setSubmissionError(null);
      dispatch(closeDialog());
      setStep(1);
      setCapturedImage(null);
    } catch (error) {
      if (error instanceof KycUpgradeError) {
        const isProc360 = error.source === "Proc360";
        const message = error.validationMessage ?? error.message;

        if (isProc360) {
          const prev = kycErrors[values.documentType];
          const isRepeat = prev?.source === "Proc360" && prev.message === message;

          dispatch(
            recordKycAttemptError({
              documentType: values.documentType,
              source: error.source,
              message,
            })
          );

          if (isRepeat) {
            // Scenario 2: Repeat Proc360 failure
            setSubmissionError(
              "You have been temporarily restricted from verifying after two unsuccessful attempts. Please contact support for help or try again later"
            );
          } else {
            // Proc360 first attempt: Raw message for retry
            setSubmissionError(error.message);
          }
        } else {
          // Scenario 1: Non-Proc360 failure
          dispatch(
            recordKycAttemptError({
              documentType: values.documentType,
              source: error.source,
              message,
            })
          );
          setSubmissionError(
            "We were unable to complete your identity verification. Please contact support for help or try again later"
          );
        }
      } else {
        // Generic / Network Error
        dispatch(
          recordKycAttemptError({
            documentType: values.documentType,
            source: undefined,
            message: error instanceof Error ? error.message : "unknown_error",
          })
        );
        setSubmissionError(
          "We were unable to complete your identity verification. Please contact support for help or try again later"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (value: boolean) => {
    if (!value) {
      dispatch(closeDialog());
      setStep(1);
      setCapturedImage(null);
      setSubmissionError(null);
    }
  };

  const getStepTitle = () => {
    if (step === 1) return "Select Document Type";
    if (step === 2) return `Verify ${getDocumentLabel(documentType)}`;
    return "Identity Verification";
  };

  const getStepDescription = () => {
    if (step === 1) return "Choose the type of document you want to upload";
    if (step === 2) return "Enter your document details";
    return "Take a clear selfie to verify your identity";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="font-outfit max-w-lg">
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
          <DialogDescription>{getStepDescription()}</DialogDescription>
        </DialogHeader>

        <Formik<KycFormFields>
          enableReinitialize
          validationSchema={
            step === 1 ? step1Schema : step === 2 ? step2Schema : step3Schema
          }
          initialValues={{
            documentVal: "",
            user_id: userId ?? "",
            tier: "platinum",
            documentType: documentOptions[0]?.value || ("" as GovernmentID),
          }}
          onSubmit={(values, { setSubmitting }) => {
            if (step === 1) {
              setStep(2);
              setDocumentType(values.documentType);
              setSubmissionError(null);
              setSubmitting(false);
            } else if (step === 2) {
              setStep(3);
              setSubmitting(false);
            } else {
              handleSubmitData(values, setSubmitting);
            }
          }}
        >
          {({ values, setFieldValue, errors, touched, handleSubmit, isSubmitting }) => (
            <div
              className={cn(
                "w-full flex flex-col gap-6 justify-between",
                step === 3 && "h-[80vh]",
              )}
            >
              {step === 1 ? (
                <ScrollArea className="h-[70vh]">
                  <div className="flex flex-col gap-4 mx-2">
                    {documentOptions.length === 0 ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl text-sm text-center">
                        All verification options are temporarily locked due to recent failures. Please contact support or try again later.
                      </div>
                    ) : (
                      <RadioGroup
                        name="documentType"
                        label="Document Type"
                        options={documentOptions}
                        required
                        horizontal={false}
                      />
                    )}
                    <Button
                      type="button"
                      className="w-full rounded-full"
                      disabled={documentOptions.length === 0}
                      onClick={() => handleSubmit()}
                    >
                      Next
                    </Button>
                    <Trust />
                  </div>
                </ScrollArea>
              ) : step === 2 ? (
                <>
                  <TextInput
                    placeholder={`Enter your ${getDocumentLabel(values.documentType)}`}
                    name="documentVal"
                    label={getDocumentLabel(values.documentType) || ""}
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() => {
                        setStep(1);
                        setFieldValue("documentVal", "");
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      className="w-full rounded-full"
                      onClick={() => handleSubmit()}
                    >
                      Next
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <ScrollArea className="h-[70vh]">
                    <div className="flex flex-col gap-4 mx-2">
                      {submissionError && (
                        <div
                          className={`p-3 border rounded-xl text-sm ${
                            isOptionCurrentlyLocked
                              ? "bg-red-50 border-destructive-foreground text-destructive"
                              : "bg-amber-50 border-amber-200 text-amber-800"
                          }`}
                        >
                          <p className="font-semibold mb-1">
                            {isOptionCurrentlyLocked
                              ? activeLock?.reason === "proc360_repeat" ||
                                submissionError.includes("unsuccessful attempts")
                                ? "Verification is temporarily locked"
                                : "Verification is temporarily unavailable"
                              : "Verification failed"}
                          </p>
                          <p>{submissionError}</p>
                        </div>
                      )}

                      <CameraCapture
                        onCapture={(file) => {
                          const imageUrl = URL.createObjectURL(file);
                          setCapturedImage(imageUrl);
                          setFieldValue("kycImage", { type: "file", file });
                          setSubmissionError(null);
                        }}
                        capturedImage={capturedImage}
                        onRetake={() => {
                          setCapturedImage(null);
                          setFieldValue("kycImage", undefined);
                          setSubmissionError(null);
                        }}
                      />
                      {errors.kycImage && touched.kycImage && (
                        <div className="text-sm text-red-600">
                          {errors.kycImage as string}
                        </div>
                      )}
                      <div className="flex flex-col gap-2 flex-shrink-0 rounded-xl p-3 justify-start bg-[#EFF6FF]">
                        <h4>Tips for a clear photo:</h4>
                        <ul className="text-sm leading-6 text-[#717182] pl-4">
                          <li>Ensure good lighting on your face</li>
                          <li>Remove glasses and hats</li>
                          <li>Look directly at the camera</li>
                          <li>Keep a natural expression</li>
                        </ul>
                      </div>
                      <Trust />
                    </div>
                  </ScrollArea>
                  <DialogFooter>
                    <div className="flex gap-3 w-full">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-full"
                        disabled={isSubmitting}
                        onClick={() => {
                          setStep(2);
                          setCapturedImage(null);
                          setSubmissionError(null);
                          setFieldValue("kycImage", undefined);
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        className="w-full rounded-full"
                        disabled={!values.kycImage || isSubmitting || isOptionCurrentlyLocked}
                        onClick={() => handleSubmit()}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Verification"}
                      </Button>
                    </div>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}